import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Linkedin, Video, FileText, ChevronRight,
  Briefcase, HelpCircle, Mail, ArrowLeft
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopHeaderBar from '../components/TopHeaderBar';
import { useAuth } from '../context/AuthContext';

const MERCOR_PURPLE = '#5B3DC5';

const TABS = [
  { id: 'contracts', label: 'Contracts', count: 0 },
  { id: 'offers', label: 'Offers', count: 0 },
  { id: 'applications', label: 'Applications', count: 3 },
  { id: 'assessments', label: 'Assessments', count: 3 },
  { id: 'saved', label: 'Saved', count: null },
];

const FEATURED_ASSESSMENTS = [
  { id: 'assessments-main', title: 'Assessments', description: 'Complete assessments to unlock more opportunities. Your results are reused across roles.', gradient: 'linear-gradient(135deg, #4338CA 0%, #5B3DC5 100%)', textColor: '#fff', subColor: 'rgba(255,255,255,0.75)', btnBg: 'rgba(255,255,255,0.2)', btnBorder: 'rgba(255,255,255,0.3)', btnText: '#fff', isMain: true },
  { id: 'domain-expert', title: 'Domain Expert Interview', badge: 'CORE', duration: '14 minutes', type: 'Interview', description: 'Evaluating AI in your field.', usedBy: 3, payingUpTo: 340, gradient: 'linear-gradient(135deg, #5B3DC5 0%, #7C3AED 100%)', textColor: '#fff', subColor: '#E0D8FF', badgeBg: '#FFDA63', badgeText: '#4C1D95', btnBg: '#fff', btnText: '#5B3DC5' },
  { id: 'code-review', title: 'Code Review Session', badge: 'CORE', duration: '30 minutes', type: 'Interview', description: 'Review and debug code with an interviewer.', usedBy: 2, payingUpTo: 200, gradient: 'linear-gradient(135deg, #FF9B7B 0%, #FF7043 100%)', textColor: '#fff', subColor: 'rgba(255,255,255,0.85)', badgeBg: '#FFDA63', badgeText: '#4C1D95', btnBg: '#fff', btnText: '#FF7043' },
];

const ALL_ASSESSMENTS = [
  { category: 'General Assessments', count: 5, items: [
    { title: 'System Design Session', duration: '31 minutes', type: 'Interview', core: true, status: 'Used by 4 roles paying up to $210/hourly', pay: '$210/hourly' },
    { title: 'Engineering Demo', duration: '1h 47m', type: 'Interview', core: false, status: 'Used by 2 roles paying up to $180/hourly', pay: '$180/hourly' },
    { title: 'Tool Use Interview', duration: '20 minutes', type: 'Interview', core: true, status: 'Available for upcoming opportunities', pay: null },
    { title: 'Cognitive Reasoning', duration: '2 questions + Form', type: 'Assessment', core: false, status: 'Available for upcoming opportunities', pay: null },
    { title: 'Algorithm Interview', duration: '45 minutes', type: 'Interview', core: true, status: 'Used by 3 roles paying up to $250/hourly', pay: '$250/hourly' },
  ]},
  { category: 'Technical Assessments', count: 4, items: [
    { title: 'Full-Stack Development', duration: '45 minutes', type: 'Interview', core: true, status: 'Used by 5 roles paying up to $200/hourly', pay: '$200/hourly' },
    { title: 'ML Engineering Assessment', duration: '3 questions', type: 'Assessment', core: false, status: 'Available for upcoming opportunities', pay: null },
    { title: 'Database Optimization', duration: '30 minutes', type: 'Interview', core: false, status: 'Used by 1 role paying up to $150/hourly', pay: '$150/hourly' },
    { title: 'Cloud Architecture Review', duration: '40 minutes', type: 'Interview', core: true, status: 'Used by 2 roles paying up to $220/hourly', pay: '$220/hourly' },
  ]},
  { category: 'Humanities Assessments', count: 1, items: [
    { title: 'English Competency', duration: '20 minutes', type: 'Interview', core: false, status: 'Used by 3 roles paying up to $45/hourly', pay: '$45/hourly' },
  ]},
];

const MOCK_APPLICATIONS = [
  { id: '1', title: 'Software Engineer (Front-end)', rate: '$70 - $80/hour', type: 'Hourly contract - Mercor', startedOn: '02/21/26', stepsCompleted: 2, totalSteps: 4 },
  { id: '2', title: 'Biology (PhD)', rate: '$60 - $80/hour', type: 'Hourly contract - Mercor', startedOn: '02/21/26', stepsCompleted: 1, totalSteps: 4 },
  { id: '3', title: 'Expert Data Scientist', rate: '$100 - $150/hour', type: 'Hourly contract - Mercor', startedOn: '02/22/26', stepsCompleted: 0, totalSteps: 4 },
];

/* ═══ Help Modal ═══ */
const HelpModal = ({ isOpen, onClose }) => {
  const [view, setView] = useState('main');
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ padding: '16px' }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl w-full overflow-y-auto" style={{ borderRadius: '14px', maxWidth: '480px', maxHeight: '85vh' }}>
        <div style={{ padding: '28px 28px 24px' }}>
          {view === 'main' ? (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>How can we help?</h2>
              <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>Select a topic to find the help you need.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { key: 'applications', icon: Briefcase, iconBg: '#EDE9FE', iconColor: MERCOR_PURPLE, title: 'How do applications work?', sub: 'Application process and how to get started' },
                  { key: 'referrals', icon: FileText, iconBg: '#DCFCE7', iconColor: '#16A34A', title: 'Referral Policies', sub: "We love referrals, here's what to expect" },
                  { key: 'support', icon: Mail, iconBg: '#F3E8FF', iconColor: '#9333EA', title: 'Contact Support', sub: 'Reach out for help' },
                ].map(item => (
                  <button key={item.key} onClick={() => setView(item.key)}
                    className="w-full flex items-center text-left hover:bg-gray-50"
                    style={{ gap: '14px', padding: '14px 16px', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
                    <div className="flex items-center justify-center shrink-0"
                      style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: item.iconBg }}>
                      <item.icon style={{ width: '20px', height: '20px', color: item.iconColor }} />
                    </div>
                    <div className="min-w-0">
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{item.title}</p>
                      <p style={{ fontSize: '12px', color: '#6B7280' }}>{item.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => setView('main')} className="flex items-center hover:opacity-70"
                style={{ gap: '4px', fontSize: '13px', color: MERCOR_PURPLE, marginBottom: '16px' }}>
                <ArrowLeft style={{ width: '16px', height: '16px' }} /> Back
              </button>
              {view === 'applications' && (
                <>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '12px' }}>How do applications work?</h2>
                  <div style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.7' }}>
                    <p style={{ marginBottom: '12px' }}>To be considered for roles on Mercor, complete the following steps:</p>
                    <ol style={{ paddingLeft: '20px', listStyle: 'decimal' }}>
                      <li style={{ marginBottom: '8px' }}><strong>Upload Your Resume</strong> – make sure it's up to date</li>
                      <li style={{ marginBottom: '8px' }}><strong>Take the AI Interview</strong> – a short assessment</li>
                      <li style={{ marginBottom: '8px' }}><strong>Answer a Few Questions</strong> – role-specific questions</li>
                      <li><strong>Application Review</strong> – we'll review and get back to you</li>
                    </ol>
                  </div>
                </>
              )}
              {view === 'referrals' && (
                <>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '12px' }}>Referral Policies</h2>
                  <p style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.7' }}>
                    When you refer someone who gets hired through Mercor, you earn a referral bonus.
                    Track your referrals and earnings in the Referrals section of your dashboard.
                  </p>
                </>
              )}
              {view === 'support' && (
                <>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '12px' }}>Contact Support</h2>
                  <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>Email</p>
                    <p style={{ fontSize: '13px', color: '#6B7280' }}>support@mercor.com</p>
                  </div>
                </>
              )}
            </>
          )}
          <button onClick={onClose} className="w-full text-white font-semibold hover:opacity-90 transition-opacity"
            style={{ marginTop: '20px', padding: '10px 0', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══ Main ═══ */
const HomeDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('contracts');
  const [helpOpen, setHelpOpen] = useState(false);
  const userName = user?.name?.split(' ')[0] || 'Tejas';

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Sidebar />
      <div className="flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
        <style>{`@media(min-width:768px){.home-main{margin-left:60px!important;padding-top:0!important;padding-bottom:0!important}}`}</style>
        <div className="home-main flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
          <TopHeaderBar />
          <div className="flex-1 overflow-y-auto">
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>

              {/* ─── Welcome ─── */}
              <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#1A1A1A', marginBottom: '20px', lineHeight: '1.2' }}>
                Welcome back, {userName}!
              </h1>

              {/* ─── Important Tasks ─── */}
              <div style={{ marginBottom: '24px' }}>
                <div className="flex items-center" style={{ gap: '6px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>Important Tasks</span>
                  <span style={{ fontSize: '13px', color: '#9CA3AF' }}>(1)</span>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center"
                  style={{ gap: '16px', padding: '16px 20px', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: '#FFFFFF' }}>
                  <div className="flex items-center justify-center shrink-0"
                    style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#EDE9FE' }}>
                    <Linkedin style={{ width: '22px', height: '22px', color: '#6B7280' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A' }}>Link LinkedIn</p>
                    <p style={{ fontSize: '13px', color: '#6B7280' }}>Connect your LinkedIn account to your Mercor account.</p>
                  </div>
                  <button className="shrink-0 text-white font-semibold hover:opacity-90 transition-opacity"
                    style={{ padding: '8px 20px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px' }}>
                    Link now
                  </button>
                </div>
              </div>

              {/* ─── Tabs ─── */}
              <div style={{ borderBottom: '1px solid #E5E7EB', marginBottom: '24px' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center overflow-x-auto scrollbar-hide" style={{ gap: '0px' }}>
                    {TABS.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                          className="relative whitespace-nowrap shrink-0"
                          style={{
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: isActive ? '#1A1A1A' : '#9CA3AF',
                            borderBottom: isActive ? `2px solid ${MERCOR_PURPLE}` : '2px solid transparent',
                            marginBottom: '-1px',
                          }}>
                          {tab.label}
                          {tab.count !== null && tab.count > 0 && (
                            <span style={{
                              marginLeft: '6px', padding: '1px 6px', fontSize: '11px', fontWeight: 600,
                              borderRadius: '4px',
                              backgroundColor: isActive ? '#EDE9FE' : '#F3F4F6',
                              color: isActive ? MERCOR_PURPLE : '#6B7280',
                            }}>{tab.count}</span>
                          )}
                          {tab.count !== null && tab.count === 0 && <span style={{ marginLeft: '4px', fontSize: '12px', color: '#9CA3AF' }}>{tab.count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="hidden sm:flex items-center shrink-0" style={{ gap: '20px' }}>
                    <button onClick={() => setHelpOpen(true)} style={{ fontSize: '13px', color: '#9CA3AF' }} className="hover:text-gray-600">Support</button>
                    <button onClick={() => navigate('/settings')} style={{ fontSize: '13px', color: '#9CA3AF' }} className="hover:text-gray-600">Settings</button>
                  </div>
                </div>
              </div>

              {/* ─── Contracts ─── */}
              {activeTab === 'contracts' && (
                <div className="flex flex-col items-center justify-center text-center" style={{ padding: '80px 0' }}>
                  <Briefcase style={{ width: '48px', height: '48px', color: '#D1D5DB', marginBottom: '16px' }} strokeWidth={1.2} />
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>You don't have any contracts yet.</p>
                </div>
              )}

              {/* ─── Offers ─── */}
              {activeTab === 'offers' && (
                <div className="flex flex-col items-center justify-center text-center" style={{ padding: '80px 0' }}>
                  <FileText style={{ width: '48px', height: '48px', color: '#D1D5DB', marginBottom: '16px' }} strokeWidth={1.2} />
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>You don't have any offers yet.</p>
                </div>
              )}

              {/* ─── Applications ─── */}
              {activeTab === 'applications' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {MOCK_APPLICATIONS.map((app) => {
                    const progress = Math.round((app.stepsCompleted / app.totalSteps) * 100);
                    return (
                      <div key={app.id} className="flex flex-col sm:flex-row sm:items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`/apply/${app.id}`)}
                        style={{ padding: '16px 20px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                        <div className="flex-1" style={{ marginBottom: '10px' }}>
                          <p style={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A' }}>{app.title}</p>
                          <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>{app.rate} · {app.type}</p>
                          <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>Started on {app.startedOn}</p>
                        </div>
                        <div className="flex items-center shrink-0" style={{ gap: '12px' }}>
                          <div>
                            <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px', textAlign: 'right' }}>{app.stepsCompleted} of {app.totalSteps} steps</p>
                            <div style={{ width: '140px', height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px' }}>
                              <div style={{ width: `${progress}%`, height: '100%', borderRadius: '3px', backgroundColor: MERCOR_PURPLE }} />
                            </div>
                          </div>
                          <ChevronRight style={{ width: '16px', height: '16px', color: '#D1D5DB' }} className="hidden sm:block" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ─── Assessments ─── */}
              {activeTab === 'assessments' && (
                <div>
                  {/* Featured cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px', marginBottom: '40px' }}>
                    {FEATURED_ASSESSMENTS.map((card) => (
                      <div key={card.id} className="flex flex-col justify-between"
                        style={{ background: card.gradient, borderRadius: '14px', padding: '24px', minHeight: '200px' }}>
                        <div>
                          {card.isMain ? (
                            <>
                              <h3 style={{ fontSize: '22px', fontWeight: 700, color: card.textColor, marginBottom: '8px' }}>{card.title}</h3>
                              <p style={{ fontSize: '13px', color: card.subColor, lineHeight: '1.5' }}>{card.description}</p>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center" style={{ gap: '8px', marginBottom: '8px' }}>
                                <Video style={{ width: '18px', height: '18px', color: card.textColor }} />
                                {card.badge && <span style={{ padding: '2px 6px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderRadius: '3px', backgroundColor: card.badgeBg, color: card.badgeText }}>{card.badge}</span>}
                              </div>
                              <h3 style={{ fontSize: '16px', fontWeight: 700, color: card.textColor, marginBottom: '4px' }}>{card.title}</h3>
                              <p style={{ fontSize: '12px', color: card.subColor, marginBottom: '4px' }}>{card.duration} · {card.type}</p>
                              <p style={{ fontSize: '13px', color: card.subColor }}>{card.description}</p>
                            </>
                          )}
                        </div>
                        <div style={{ marginTop: '16px' }}>
                          {!card.isMain && <p style={{ fontSize: '11px', color: card.subColor, marginBottom: '10px' }}>Used by {card.usedBy} roles · Up to ${card.payingUpTo}/hr</p>}
                          <button className="hover:opacity-90 transition-opacity"
                            style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, borderRadius: '8px', backgroundColor: card.btnBg, color: card.btnText, border: card.btnBorder ? `1px solid ${card.btnBorder}` : 'none' }}>
                            {card.isMain ? 'View all' : 'Start interview'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Assessment lists */}
                  {ALL_ASSESSMENTS.map((category, ci) => (
                    <div key={ci} style={{ marginBottom: '32px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A', marginBottom: '12px' }}>
                        {category.category} <span style={{ fontWeight: 400, color: '#9CA3AF' }}>({category.count})</span>
                      </h3>
                      <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
                        {category.items.map((item, ii) => (
                          <div key={ii} className="flex flex-col sm:flex-row sm:items-center justify-between hover:bg-gray-50/50"
                            style={{ padding: '12px 20px', borderBottom: ii < category.items.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                            <div className="flex items-center" style={{ gap: '12px', marginBottom: '6px' }}>
                              <div className="flex items-center justify-center shrink-0"
                                style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#EDE9FE' }}>
                                <Video style={{ width: '16px', height: '16px', color: MERCOR_PURPLE }} />
                              </div>
                              <div>
                                <div className="flex items-center flex-wrap" style={{ gap: '6px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{item.title}</span>
                                  {item.core && <span style={{ padding: '1px 5px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', borderRadius: '3px', backgroundColor: '#FFDA63', color: '#4C1D95' }}>Core</span>}
                                </div>
                                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{item.duration} · {item.type}</p>
                              </div>
                            </div>
                            <div className="flex items-center" style={{ gap: '12px', marginLeft: '48px' }}>
                              <div className="text-right hidden sm:block">
                                <p style={{ fontSize: '12px', color: '#6B7280' }}>{item.status}</p>
                              </div>
                              <button className="hover:opacity-80"
                                style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', color: MERCOR_PURPLE, border: `1px solid #D8B4FE` }}>
                                {item.type === 'Interview' ? 'Start Interview' : 'Start Assessment'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ─── Saved ─── */}
              {activeTab === 'saved' && (
                <div className="flex flex-col items-center justify-center text-center" style={{ padding: '80px 0' }}>
                  <Briefcase style={{ width: '48px', height: '48px', color: '#D1D5DB', marginBottom: '16px' }} strokeWidth={1.2} />
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>You don't have any saved items yet.</p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Floating chat button */}
      <button onClick={() => setHelpOpen(true)}
        className="fixed flex items-center justify-center text-white rounded-full shadow-lg hover:opacity-90 z-50"
        style={{ bottom: '24px', right: '24px', width: '48px', height: '48px', backgroundColor: MERCOR_PURPLE }}>
        <HelpCircle style={{ width: '24px', height: '24px' }} />
      </button>
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};

export default HomeDashboard;
